
import { i18n } from "@lingui/core";
import { t, Trans } from '@lingui/macro';
import { Price, Product, Subscription } from '@invertase/firestore-stripe-payments';
import { CheckCircle, Loader2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { GetTiers } from '../../../const/TierData';
import { useAppUIContext } from '../../../context/AppUIContext';
import { Tier } from '../../../models/local/Tiers';
import { Currency } from '../../../models/local/Types';
import { Workspace } from '../../../models/Workspace';
import { WorkspaceStatus } from '../../../models/WorkspaceStatus';
import { Fire } from '../../../services/FireClient';
import { My } from '../../../services/My';
import { pojo, Util } from '../../../services/Utils/Util';
import { UtilApp } from '../../../services/Utils/UtilApp';
import { Capacitor } from '@capacitor/core';
import PremiumWSIcon from '../../components/PremiumWSIcon';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { useToast } from "../../../components/ui/use-toast";
import { cn } from "../../../lib/utils";

type SubbedProduct = { sub: Subscription, prod: Product | undefined, price: Price | undefined };

const WSAdminSubscriptionPage: React.FC = () => {
  const { workspaces } = useAppUIContext();
  const [workspace, setWorkspace] = useState<Workspace>();
  const [inProgress, setInProgress] = useState(false);
  const [inProgressMoving, setInProgressMoving] = useState(false);
  const [currency, setCurrency] = useState<Currency>(i18n.locale === 'ja' ? 'jpy' : (i18n.locale == 'ko' ? 'krw' : 'usd'));
  const { toast } = useToast();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [subProducts, setSubbedProducts] = useState<SubbedProduct[]>([]);
  const [hasActiveSub, setHasActiveSub] = useState<boolean>();
  const [wsStatus, setWSStatus] = useState<WorkspaceStatus>();

  // Alert Dialog State
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    description: string;
    actionLabel?: string;
    onAction?: () => void;
    cancelLabel?: string;
  }>({ title: "", description: "" });

  useEffect(() => {
    setSubbedProducts([]);
    const ws = workspaces.find(w => w.id === workspaceId);
    setWorkspace(ws ? pojo(ws) as Workspace : undefined);
    if (ws) {
      fetchActiveSubAndStatus();
    }
  }, [workspaces, workspaceId]);

  async function fetchActiveSubAndStatus() {
    setInProgress(true);
    setSubbedProducts([]);
    setHasActiveSub(false);
    try {
      const subs = await Fire.payment.getUserSubscriptions(workspaceId || "");
      setHasActiveSub(subs.length > 0);

      const list: SubbedProduct[] = [];
      for (let i = 0; i < subs.length; i++) {
        const sub = subs[i];
        if (!list.find(l => l.sub.id === sub.id)) { // of some reason, there are duplicates sometimes.
          const prod = await Fire.payment.getProduct(sub.product);
          const price = await Fire.payment.getPrice(sub.product, sub.price);
          list.push({ sub, prod, price });
        }
      }
      setSubbedProducts(list);
      setWSStatus(await Fire.ws.getWorkspaceStatus(workspaceId || ""));
    } catch (error) {
      console.error(error);
      showInfo(t`Error`, (error as Error).message);
    } finally {
      setInProgress(false);
    }
  }

  const showAlert = (title: string, description: string, actionLabel?: string, onAction?: () => void, cancelLabel: string = t`Cancel`) => {
    setAlertConfig({ title, description, actionLabel, onAction, cancelLabel });
    setAlertOpen(true);
  };

  const showInfo = (title: string, description: string) => {
    setAlertConfig({ title, description, cancelLabel: t`OK` });
    setAlertOpen(true);
  }

  async function startTrial() {
    showAlert(
      t`Start Trial`,
      t`You will start a 7-day trial of the Premium tier. Do you want to continue?`,
      t`Continue`,
      () => startTrialFunc()
    );
  }

  async function startTrialFunc() {
    setInProgress(true);
    const r = await Fire.payment.startPremiumTrial(workspaceId || "");
    setInProgress(false);
    if (r.result == 'ONLY_FREE_ALLOWED') {
      showInfo(t`Info`, t`Only free workspace can start a trial.`);
    } else if (r.result === 'TRIAL_ALREADY_STARTED') {
      showInfo(t`Info`, t`You have already started a trial.`);
    } else if (r.result === 'SUCCESS') {
      showInfo(t`Info`, t`You have now started a trial.`);
    }
  }

  function manageSubscription() {
    showAlert(
      t`Manage Subscription`,
      t`You will move to your Billing Portal to manage your subscription. Do you want to continue?`,
      t`Continue`,
      () => moveToCustomerPortalPage()
    );
  }

  async function changeToTier(tier: Tier) {
    if (!workspace) return;
    if (isMobileAndShowWarningIfSo()) return;

    if (hasActiveSub) {
      manageSubscription();
    } else {
      showAlert(
        t`Go to the Billing Page`,
        t`You will now go to the Billing Page to complete your subscription.`,
        t`Continue`,
        () => moveToCheckoutPage(tier, workspace)
      );
    }
  }

  async function moveToCheckoutPage(tier: Tier, workspace: Workspace) {
    const priceId = Tier.getStripePriceID(tier, currency, 'monthly');
    setInProgress(true);
    setInProgressMoving(true);
    try {
      await Fire.payment.moveToCheckoutPage(priceId, workspace.id, workspace.name, My.user.id);
    } catch (error) {
      const e = error as Error;
      showInfo(t`Error`, e.message);
    }
    setInProgress(false);
    setInProgressMoving(false);
  }

  async function moveToCustomerPortalPage() {
    if (isMobileAndShowWarningIfSo()) return;

    setInProgress(true);
    setInProgressMoving(true);
    try {
      await Fire.payment.manageSubscription();
    } catch (error) {
      const e = error as Error;
      showInfo(t`Error`, e.message);
    }
    setInProgress(false);
    setInProgressMoving(false);
  }

  function canStartTrial() {
    return workspace?.tier === 'FREE' && !wsStatus?.trialStart;
  }

  function isMobileAndShowWarningIfSo() {
    if (Capacitor.isNativePlatform()) {
      showInfo(
        t`The billing page is unavailable in the mobile app`,
        t`Use NEXTPOD on browser (${Util.getAppUrl()}) to manage your subscription.`
      );
      return true;
    } else {
      return false;
    }
  }

  function getTierCard(t: Tier) {
    return (
      <Card key={t.tier} className={cn("flex flex-col", t.tier === 'PREMIUM' ? "border-primary/50 shadow-md" : "")}>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            {t.name}
          </CardTitle>
          <CardDescription>
            <span className="text-lg font-bold text-foreground">
              <Trans>{Tier.getPrice(t, currency)}</Trans>
            </span>
            <small className="text-muted-foreground">/month</small>
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <ul className="space-y-2">
            {t.features.map(f => (
              <li key={f} className="flex items-start gap-2 text-sm">
                {!f.includes('features') && <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />}
                <span className={cn(f.includes('features') ? "font-semibold" : "")}>{f}</span>
              </li>
            ))}
          </ul>
        </CardContent>
        <CardFooter>
          {!hasActiveSub && t.tier !== 'FREE' && (
            <Button
              className="w-full"
              variant={t.tier === 'PREMIUM' ? 'default' : 'outline'}
              onClick={() => changeToTier(t)}
            >
              <Trans>Upgrade to {t.name}</Trans>
            </Button>
          )}
        </CardFooter>
      </Card>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-background overflow-hidden">
      {/* Header Area */}
      <div className="border-b p-4 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          {/* Reimplementing WSHeaderTitle logic */}
          <Users className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-lg font-semibold truncate flex-1">{workspace?.name}</h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>id: {workspace?.id}</span>
          <PremiumWSIcon ws={workspace} />
        </div>
        {inProgress && <div className="h-1 w-full bg-primary/20 overflow-hidden rounded-full"><div className="h-full bg-primary animate-progress origin-left"></div></div>}
      </div>

      {inProgressMoving ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-4">
          <h3 className="text-xl font-semibold"><Trans>Moving to the Billing Page</Trans></h3>
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-4 md:p-6 space-y-6">
          {!inProgress && workspace && (
            <>
              {/* Status Cards */}
              {wsStatus?.tier === 'PREMIUM_TRIAL' && (
                <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200">
                  <CardContent className="p-4 flex items-center gap-2 text-blue-700 dark:text-blue-300">
                    <Trans>Your Premium Trial period ends {UtilApp.dateStrToDay(wsStatus?.trialEnd, My.user.lang)}</Trans>
                  </CardContent>
                </Card>
              )}
              {Tier.hasTrialExpired(wsStatus) && (
                <Card className="bg-yellow-50 dark:bg-yellow-950 border-yellow-200">
                  <CardContent className="p-4 flex items-center gap-2 text-yellow-700 dark:text-yellow-300">
                    <Trans>Your Premium Trial period has expired. Upgrade to a tier below.</Trans>
                  </CardContent>
                </Card>
              )}

              {/* Active Subscriptions */}
              {subProducts.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium"><Trans>Current Subscription</Trans></h4>
                  {subProducts.map(sp => (
                    <Card key={sp.sub.id}>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <div className="flex flex-col">
                          <CardTitle className="text-base">{sp.prod?.name}</CardTitle>
                          <CardDescription>{sp.sub.metadata['workspaceId']}</CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => manageSubscription()}>
                          <Trans>Manage</Trans>
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-1 text-sm">
                        <div>
                          <Trans>Current Period: {Util.toDateString(sp.sub.current_period_start)} to {Util.toDateString(sp.sub.current_period_end)}</Trans>
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">{sp.sub.status}</span>
                        </div>
                        <div>
                          <Trans>Cost:</Trans> {sp.price?.unit_amount?.toLocaleString()} {sp.price?.currency}/{sp.price?.interval}
                        </div>
                        {sp.sub.cancel_at_period_end && (
                          <div className="text-muted-foreground">
                            <Trans>Will cancel at the end of period</Trans> ({Util.toDateString(sp.sub.cancel_at)})
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Trial Offer */}
              {canStartTrial() && (
                <Card>
                  <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <span className="font-medium"><Trans>Try Premium workspace for free with a 7-day trial.</Trans></span>
                    <Button onClick={startTrial}><Trans>Start Trial</Trans></Button>
                  </CardContent>
                </Card>
              )}

              {/* Tiers List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium"><Trans>Available Tiers</Trans></h4>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground hidden sm:inline"><Trans>Selected currency:</Trans></span>
                    <Select value={currency} onValueChange={(v: Currency) => setCurrency(v)}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="krw"><Trans>Korean Won</Trans></SelectItem>
                        <SelectItem value="jpy"><Trans>Japanese Yen</Trans></SelectItem>
                        <SelectItem value="usd"><Trans>US Dollar</Trans></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {GetTiers().map(t => getTierCard(t))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Alert Dialog */}
      <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertConfig.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {alertConfig.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/* If there is no action, just show confirm/cancel as OK */}
            {!alertConfig.onAction ? (
              <AlertDialogAction onClick={() => setAlertOpen(false)}>{alertConfig.cancelLabel}</AlertDialogAction>
            ) : (
              <>
                <AlertDialogCancel onClick={() => setAlertOpen(false)}>{alertConfig.cancelLabel}</AlertDialogCancel>
                <AlertDialogAction onClick={() => {
                  alertConfig.onAction?.();
                  setAlertOpen(false);
                }}>{alertConfig.actionLabel}</AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WSAdminSubscriptionPage;



{/* {subProducts.length > 1 && <IonItem color='warning'><Trans>Oops, you have more than one active subscription. Please contact contact@sunbisoft.com for trouble shooting.</Trans></IonItem>} */ }
{/* {workspace.tier !== sp.prod?.metadata['tier'] && <IonItem color='danger'>TODO: Update workspace {workspace.tier} to {sp.prod?.metadata['tier']}</IonItem>} */ }
{/* {sp.sub.cancel_at && <IonItem><Trans>Cancel at: {Util.toDateString(sp.sub.cancel_at)}</Trans></IonItem>} */ }
{/* <IonSelectOption value="eur"><Trans>Euro</Trans></IonSelectOption> */ }
